import { fromBinary } from "@bufbuild/protobuf";
import { BroadcastListSchema, EntityType, type BroadcastEntry, type BroadcastList, type Edition, type Game, type Player, type PlayableItem, type Review } from "./gen/catalog_pb.js";
import { catalogBestEffortClient, catalogClient } from "./net/clients.js";
import { raceAbort } from "./net/retry.js";
import { CALENDAR_TRANSPORT, CATALOG_HOST, FINISHED_WINDOW_SECS, TOUR_IDS } from "./config.js";
import { hotBoards } from "./live/hot-boards.js";
import type { TopGame } from "./ui/top-games.js";

/**
 * The site's anonymous catalog reads: a plain HTTP calendar document and
 * unary RPCs for discovery/detail lookups.
 *
 * Round discovery deliberately does NOT use `Broadcast.ListRounds`: it is empty
 * in worker mode (#1803), which is the mode staging runs in. `ListBroadcasts`
 * is the overlay-fed calendar and is the only surface that knows what is on.
 */

export type BroadcastState = "active" | "upcoming" | "finished";

export interface CalendarEntry {
  entry: BroadcastEntry;
  state: BroadcastState;
  startsAtMs: number | null;
  /**
   * `BroadcastEntry.edition_id` — "ed:<slug>", present once the round is
   * archived (`proto/catalog.proto:631`), null for most of a round's life.
   *
   * This is the ONE ref the calendar carries and the only one it may carry:
   * `catalog.proto` invariant 3 keeps `ListBroadcasts.editions[]` refs empty,
   * so the calendar is a NAV surface, not a content surface. The hub navigates
   * by this id and the edition page loads `GetEdition`, which is the message
   * that carries pack/experience refs. Adding an `editionRef` here would
   * reverse the invariant (PR #2177 review R4).
   *
   * It is a NAV id only. It is NOT the publication signal: `edition_id_of()`
   * in `relay-control/src/calendar.rs` mints one unconditionally for every
   * discovered round, so it is set from first discovery and says nothing about
   * whether anything was published. Use `isPublished`.
   */
  editionId: string | null;

  /**
   * Has the catalog actually published this round's edition?
   *
   * The server's own answer, not an inference: `ListBroadcasts` probes the
   * Editions namespace for each entry's `edition_id` and puts a ref in the
   * response's `editions` dictionary only on a hit, logging "edition probe
   * miss" otherwise (`content-service/src/handlers/calendar.rs`). Membership in
   * that map therefore means "an edition doc exists", which is what a finished
   * round's pill is asserting when it says the round is served.
   *
   * The probe is unchanged by the calendar-object redesign (#2214): whether the
   * overlay was fed by `/overlay/push` or polled from `calendar/<env>/overlay.pb`
   * in R2, `list_broadcasts` probes the same namespace at read time.
   *
   * It says "an edition doc exists", not "this round has started content" —
   * `ListBroadcasts` carries no per-round content (`EditionRef.game_count` is
   * `doc.tracks.len()`, the edition's games, not the started ones), and this
   * surface is built to decide a hub row without a second RPC. The residual
   * overclaim is an edition doc whose rounds have nothing started; the case this
   * closes is the id being minted at discovery for every round that ever
   * existed. If the finer signal is wanted, it belongs on the entry, computed
   * server-side, in the spirit of #2214's `overlay_seeded` / `calendar_age_seconds`.
   */
  isPublished: boolean;
}

/**
 * A proto `optional int64` as a JavaScript number — absence stays absence.
 *
 * The ONE place the wire's `bigint | undefined` becomes `number | null`, for
 * every epoch/duration field the calendar carries: a round's `starts_at_ms`,
 * the tour's `tour_starts_at_ms` / `tour_ends_at_ms` (read by `tours.ts`) and
 * the list's `calendar_age_seconds`. A second `=== undefined ? null : Number()`
 * at a call site is how one of them quietly starts reading `0` for "absent".
 */
export const optionalInt64 = (v: bigint | undefined): number | null => (v === undefined ? null : Number(v));

const startsAt = (e: BroadcastEntry): number | null => optionalInt64(e.startsAtMs);

/** A present-but-blank optional string is absence, not a value. */
export const optional = (v: string | undefined): string | null => {
  const trimmed = (v ?? "").trim();
  return trimmed.length === 0 ? null : trimmed;
};

/**
 * One wire `BroadcastEntry` as the site's calendar sees it.
 *
 * The single place a `BroadcastEntry` becomes a `CalendarEntry`, so the
 * tolerance rules for its optional fields (absent start, absent edition id) are
 * stated once and tested once — `catalog.entry.test.ts`.
 *
 * `publishedIds` is the key set of the response's `editions` dictionary. It is
 * passed in rather than read here because it belongs to the RESPONSE, not to
 * the entry: one probe result serves every entry in the same payload.
 */
export const toCalendarEntry = (
  entry: BroadcastEntry,
  state: BroadcastState,
  publishedIds: ReadonlySet<string> = new Set(),
): CalendarEntry => {
  const editionId = optional(entry.editionId);
  return {
    entry,
    state,
    startsAtMs: startsAt(entry),
    editionId,
    isPublished: editionId !== null && publishedIds.has(editionId),
  };
};

/**
 * Is this broadcast's tour one the site shows?
 *
 * Membership, not equality: the GCL 2026 runs as two Lichess tours (Preliminary
 * Stage `B6gJ5CBc` + Finals `uacsaXZb`), and an equality filter dropped every
 * Finals round on the floor. An EMPTY list is the documented "no filter"
 * setting, so it keeps everything.
 */
export const inTours = (tourId: string, tourIds: readonly string[]): boolean =>
  tourIds.length === 0 || tourIds.includes(tourId);

/**
 * Today's card for the configured tours, ordered the way a viewer reads it:
 * what is on now (with a live dot), then what is next by start time, then what
 * just ended.
 *
 * `page_token` is ignored by the server and `next_page_token` is always empty,
 * so there is deliberately no paging loop here — asking for one would be a lie
 * about the contract.
 */
export async function fetchCalendar(tourIds: readonly string[] = TOUR_IDS, signal?: AbortSignal): Promise<CalendarEntry[]> {
  return (await fetchCalendarSnapshot(tourIds, signal)).entries;
}

/**
 * One `ListBroadcasts` answer INCLUDING what the response says about itself.
 *
 * `overlay_seeded` and `calendar_age_seconds` are list-level facts the
 * content-service added with the calendar-object redesign (#2214/#2377): the
 * first says whether the overlay has ever been fed at all, the second how old
 * the object it answered from is. They were dropped on the floor here, which
 * left every caller unable to tell "the calendar answered: nothing is on" from
 * "the calendar has never been fed" — the exact conflation
 * `HomeSchedulePresentation.Unavailable` exists to prevent in the app.
 *
 * mirror-of: `shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HomeSchedulePresentation.kt`
 * (`HomeScheduleStrip.Resolved` may be EMPTY; only `Unavailable` licenses a
 * placeholder). The web's reading of that split is `tourBoard` in `tours.ts`,
 * and `tours.test.ts` is its agreement test.
 */
export interface CalendarSnapshot {
  entries: CalendarEntry[];
  /** `BroadcastList.overlay_seeded` — has the overlay ever been fed? */
  overlaySeeded: boolean;
  /** `BroadcastList.calendar_age_seconds`, or null when the server sent none. */
  calendarAgeSeconds: number | null;
  /**
   * `BroadcastList.hot_boards` through {@link hotBoards}: the Heating Up
   * producer's picks in wire order, guarded against the UNFILTERED `active`
   * rounds of this same response (as the app does). Empty = nothing to show.
   */
  topGames: readonly TopGame[];
}

/**
 * One `BroadcastList` read, by the build's {@link CALENDAR_TRANSPORT}: the
 * unary grpc-web RPC (default; the catalog transport's bounded retry,
 * `net/retry.ts`, applies) or the cacheable `GET /v1/calendar.pb` (#2580).
 * The GET is opt-in because the backend route must be deployed first
 * (`docs/ops/calendar-http-get-2026-09.md`); it deliberately has no RPC
 * fallback and no transport retry, so a missing route is visible, and the
 * homepage's `watchCalendar` re-asks either way.
 *
 * `cacheAgeSeconds` is the HTTP `Age` a CDN hit adds; 0 on the RPC path.
 */
async function readBroadcastList(signal?: AbortSignal): Promise<{ res: BroadcastList; cacheAgeSeconds: number }> {
  if (CALENDAR_TRANSPORT !== "http-get") {
    const res = await catalogClient.listBroadcasts(
      { finishedWindowSecs: FINISHED_WINDOW_SECS, pageSize: 0, pageToken: "" },
      signal ? { signal } : {},
    );
    return { res, cacheAgeSeconds: 0 };
  }
  const url = new URL("/v1/calendar.pb", CATALOG_HOST);
  url.searchParams.set("finished_window_secs", String(FINISHED_WINDOW_SECS));
  const response = await fetch(url, { signal, credentials: "omit" });
  if (!response.ok) throw new Error(`Calendar HTTP ${response.status}`);
  if (response.headers.get("content-type")?.split(";", 1)[0]?.trim() !== "application/x-protobuf") {
    throw new Error("Calendar response is not a protobuf document");
  }
  const res = fromBinary(BroadcastListSchema, new Uint8Array(await response.arrayBuffer()));
  // The body reports age at origin assembly. A CDN HIT adds residence time.
  const ageHeader = response.headers.get("age") ?? "";
  const cacheAge = /^\d+$/u.test(ageHeader) ? Number(ageHeader) : 0;
  return { res, cacheAgeSeconds: Number.isSafeInteger(cacheAge) ? cacheAge : 0 };
}

/** {@link fetchCalendar}, plus the list-level fields. The one calendar read site. */
export async function fetchCalendarSnapshot(
  tourIds: readonly string[] = TOUR_IDS,
  signal?: AbortSignal,
): Promise<CalendarSnapshot> {
  const { res, cacheAgeSeconds } = await readBroadcastList(signal);
  // Preserve absent age rather than inventing one for legacy/unfed calendars.
  const originAge = optionalInt64(res.calendarAgeSeconds);
  // The response's own probe result: an id is in here only when the server
  // found an edition doc for it (`handlers/calendar.rs`). Read once per
  // response, not per entry.
  const publishedIds = new Set(Object.keys(res.editions));
  const pick = (list: BroadcastEntry[], state: BroadcastState): CalendarEntry[] =>
    list.filter((e) => inTours(e.tourId, tourIds)).map((entry) => toCalendarEntry(entry, state, publishedIds));

  return {
    entries: [
      ...pick(res.active, "active").sort(byStart),
      ...pick(res.upcoming, "upcoming").sort(byStart),
      ...pick(res.recentlyFinished, "finished").sort((a, b) => -byStart(a, b)),
    ],
    overlaySeeded: res.overlaySeeded,
    calendarAgeSeconds: originAge === null ? null : originAge + cacheAgeSeconds,
    topGames: hotBoards(res, Date.now()),
  };
}

export const byStart = (a: CalendarEntry, b: CalendarEntry): number =>
  (a.startsAtMs ?? Number.MAX_SAFE_INTEGER) - (b.startsAtMs ?? Number.MAX_SAFE_INTEGER);

/**
 * One `Search` call, for whichever entity types the caller shows.
 *
 * The ONE place a search request is built: the field list (`section`,
 * `federation`, `tier`, `year`) is the wire's, not a page's, and a second copy
 * of it would drift the day a filter is added. `/search` asks for editions,
 * players and games; the player directory asks for players only.
 */
export async function searchEntities(q: string, types: readonly EntityType[], signal?: AbortSignal): Promise<PlayableItem[]> {
  if (q.trim().length === 0) return [];
  const res = await catalogClient.search(
    { q, types: [...types], pageSize: 20, pageToken: "", section: "", federation: "", tier: "", year: 0 },
    signal ? { signal } : {},
  );
  return res.results;
}

/** Players only — the player directory's arm of {@link searchEntities}. */
export const searchPlayers = (q: string, signal?: AbortSignal): Promise<PlayableItem[]> =>
  searchEntities(q, [EntityType.PLAYER], signal);

/** The entity types `/search` offers. Editions and games are the archive's own surfaces. */
export const SEARCH_TYPES: readonly EntityType[] = [EntityType.EDITION, EntityType.PLAYER, EntityType.GAME];

/**
 * The edition row — the message that carries the pack and experience refs.
 *
 * `ListBroadcasts` carries none (`catalog.proto` invariant 3, review R4), so
 * this is the call an edition page makes after the hub navigates by id.
 */
export const fetchEdition = (id: string, signal?: AbortSignal): Promise<Edition> =>
  catalogClient.getEdition({ id, pageToken: "", pageSize: 200 }, signal ? { signal } : {});

/**
 * The tournament shell — the message that maps a round id to its game ids.
 *
 * The archive round page needs "which games were in THIS round", and this is
 * the only surface that answers it: the pack numbers its rounds but does not
 * know a Lichess round id, and the calendar is nav-only.
 */
export function fetchTournament(editionId: string, signal?: AbortSignal): ReturnType<typeof catalogClient.getTournament> {
  const at = Date.now();
  const hit = tournamentReads.get(editionId);
  if (hit !== undefined && at - hit.at < CALENDAR_TTL_MS) return signal ? raceAbort(hit.promise, signal) : hit.promise;
  // No caller's signal: the request is every sharer's (#3115).
  const promise = catalogClient.getTournament({ editionId });
  const entry = { at, promise };
  tournamentReads.set(editionId, entry);
  promise.catch(() => { if (tournamentReads.get(editionId) === entry) tournamentReads.delete(editionId); });
  return signal ? raceAbort(promise, signal) : promise;
}

/**
 * {@link fetchTournament}'s answers, per edition, for {@link CALENDAR_TTL_MS} — the calendar's
 * own policy (`loadCalendarSnapshot`), for the same reason: the watch page's rail, its round
 * page, its waiting room and a round switched in place all ask for the same edition within
 * seconds (measured on the Olympiad watch page: five `GetTournament` calls on one load). The
 * PROMISE is kept, so racing callers share one request; a rejection is not. The request is
 * started with NO caller's signal, because it is every sharer's: a caller's signal only stops
 * that caller waiting (`raceAbort`, CANCELED), and the request runs on for the rest (#3115).
 */
const tournamentReads = new Map<string, { at: number; promise: ReturnType<typeof catalogClient.getTournament> }>();

/** Drop the memoised tournament reads. Tests only — a page load starts empty anyway. */
export function resetTournamentReads(): void {
  tournamentReads.clear();
}

/** One game row: its track (both object refs), its edition, its PGN bundle. */
export const fetchGame = (id: string, signal?: AbortSignal): Promise<Game> =>
  catalogClient.getGame({ id }, signal ? { signal } : {});

type ReviewCaller = (gameId: string, signal?: AbortSignal) => Promise<Review>;

const callReview: ReviewCaller = (gameId, signal) => catalogClient.getReview({ gameId }, signal ? { signal } : {});

/**
 * The review pointer for a game, or null when there is no recap.
 *
 * Null, not a throw: the content-service answers `NOT_FOUND` for a game with no
 * review — measured on staging 2026-09-06, `GetReview{g:2395b50d605b}` →
 * `[not_found] review not found` — and an unpublished recap is an absence, not
 * an error (#732). Every other code still rejects, because an unreachable
 * catalog is a different fact from an unwritten recap.
 */
export async function fetchReview(gameId: string, signal?: AbortSignal, caller: ReviewCaller = callReview): Promise<Review | null> {
  try {
    return await caller(gameId, signal);
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

/** A Connect `NOT_FOUND` (code 5), however the transport surfaced it. */
export function isNotFound(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return code === "not_found" || code === 5;
}

/** `bestEffort` skips the transport's retry budget, for fan-out reads whose failure
 * only loses an optional extra (the leaderboard's estimates). */
export const fetchPlayer = (id: string, signal?: AbortSignal, bestEffort = false): Promise<Player> =>
  (bestEffort ? catalogBestEffortClient : catalogClient).getPlayer({ id }, signal ? { signal } : {});

export const fetchPlayerEvents = (playerId: string, signal?: AbortSignal) =>
  catalogClient.listPlayerEvents({ playerId, pageSize: 24, pageToken: "" }, signal ? { signal } : {});

export const fetchHeadToHead = (playerA: string, playerB: string, signal?: AbortSignal) =>
  catalogClient.getHeadToHead({ playerA, playerB, maxGames: 10 }, signal ? { signal } : {});

/** One pairing read from the viewed player's side: who they played, and their own W/D/L. */
export interface OrientedHeadToHead {
  opponentId: string;
  wins: number;
  losses: number;
  draws: number;
}

/**
 * Read a pairing from ONE player's side.
 *
 * `GetHeadToHead` is order-independent and the server canonicalizes the pair
 * (`relay-server-rs/content-service/src/handlers/docs.rs:315` `h2h_key`, pinned
 * at `:582`), so `aId` is the lexically-lesser id — never "the player whose
 * page this is". Reading `aWins`/`bWins` positionally therefore shows roughly
 * half of all players their losses as wins.
 *
 * mirror-of: `shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HeadToHeadViewModel.kt:74-83`
 * (and `RivalryViewModel.kt:128-129`) — the app's own orient-by-id, which
 * `CatalogRepository.kt:249` states in words: consumers match by id, not by
 * argument position. Agreement test: `src/test/catalog.h2h.test.ts`.
 *
 * Takes the counts as optional so the counts-free `HeadToHeadRef` (the
 * `notableRivalry` seed) names its opponent through the same one definition.
 */
export function orientHeadToHead(
  pair: { aId: string; bId: string; aWins?: number; bWins?: number; draws?: number },
  viewedId: string,
): OrientedHeadToHead {
  const viewedIsA = pair.aId === viewedId;
  return {
    opponentId: viewedIsA ? pair.bId : pair.aId,
    wins: (viewedIsA ? pair.aWins : pair.bWins) ?? 0,
    losses: (viewedIsA ? pair.bWins : pair.aWins) ?? 0,
    draws: pair.draws ?? 0,
  };
}

/**
 * How long one `ListBroadcasts` answer serves every page on this tab. The
 * content-service caches the relay's catalog for 60 s upstream
 * (`relay-distributor/src/catalog.rs:1-52`), so a shorter TTL here would buy
 * nothing but requests.
 */
export const CALENDAR_TTL_MS = 60_000;

type CalendarFetcher = (tourIds: readonly string[], signal?: AbortSignal) => Promise<CalendarSnapshot>;

/** What every reader of the memoised calendar may supply: the tests' seams, and the caller's cancellation. */
export interface CalendarLoadDeps {
  fetcher?: CalendarFetcher;
  now?: () => number;
  /**
   * Stops THIS caller waiting: on abort its promise rejects `CANCELED`. It never
   * reaches the fetch, which is shared with every caller inside the TTL, so one
   * page's teardown cannot cancel another page's answer (#3115: the homepage's
   * abort on a click-through used to reach the round page's lookup, which read
   * the CANCELED as "unknown round" and subscribed a finished one). The fetch
   * and its retry chain run on and answer the memo.
   */
  signal?: AbortSignal;
}

let cached: { key: string; at: number; promise: Promise<CalendarSnapshot> } | null = null;

/** Drop the memoised calendar. Tests only — a page load starts empty anyway. */
export function resetCalendarCache(): void {
  cached = null;
}

/**
 * `fetchCalendar` memoised per tab for {@link CALENDAR_TTL_MS}.
 *
 * The round page and the home page ask the same question within a second of
 * each other on a click-through; one fetch answers both. The PROMISE is cached,
 * not the result, so two callers racing on a cold cache still share a request.
 * A rejection is not retained.
 */
export async function loadCalendar(
  tourIds: readonly string[] = TOUR_IDS,
  deps: CalendarLoadDeps = {},
): Promise<CalendarEntry[]> {
  return (await loadCalendarSnapshot(tourIds, deps)).entries;
}

/**
 * {@link loadCalendar}'s snapshot form, and the memo's real subject.
 *
 * The cache holds the SNAPSHOT because `overlay_seeded` is a property of the
 * same answer as the entries: caching the entries and re-asking for the flag
 * would be two reads of one response, able to disagree.
 *
 * An UNSEEDED answer is not retained either. It is a real answer — the page
 * shows it — but it is the cold instance's "not yet", and the homepage re-asks
 * every few seconds (`pages/home.ts`) precisely so that a seed landing ten
 * seconds later reaches the visitor; memoising it would answer every re-ask
 * from memory for the whole TTL.
 */
export function loadCalendarSnapshot(
  tourIds: readonly string[] = TOUR_IDS,
  deps: CalendarLoadDeps = {},
): Promise<CalendarSnapshot> {
  const fetcher = deps.fetcher ?? fetchCalendarSnapshot;
  const now = deps.now ?? Date.now;
  const key = tourIds.join(",");
  const at = now();
  const signal = deps.signal;
  if (cached && cached.key === key && at - cached.at < CALENDAR_TTL_MS) return signal ? raceAbort(cached.promise, signal) : cached.promise;
  // No caller's signal: the fetch is every sharer's (#3115, `CalendarLoadDeps.signal`).
  const promise = fetcher(tourIds);
  cached = { key, at, promise };
  const forget = (): void => {
    if (cached?.promise === promise) cached = null;
  };
  promise.then((snapshot) => {
    if (!snapshot.overlaySeeded) forget();
  }, forget);
  return signal ? raceAbort(promise, signal) : promise;
}

/**
 * The calendar entry for one round, or null when the calendar has never heard
 * of it (or cannot be reached).
 *
 * Null is a real answer, not an error: `Subscribe` succeeds for any round id
 * (BACKEND-RPCS §1.4), so a round page may legitimately outlive the 24 h the
 * overlay retains its entry. The caller renders the stream's own truth then.
 */
export async function findBroadcast(
  roundId: string,
  tourIds: readonly string[] = TOUR_IDS,
  deps: CalendarLoadDeps = {},
): Promise<CalendarEntry | null> {
  if (roundId.length === 0) return null;
  try {
    const calendar = await loadCalendar(tourIds, deps);
    return calendar.find((c) => c.entry.roundId === roundId) ?? null;
  } catch (err) {
    console.warn("[catalog] calendar lookup failed", err);
    return null;
  }
}
